import React from "react";
import {
  Box,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import StandardButton from "../../../../components/StandardButton";
import {
  Today as DailyIcon,
  TrendingUp as GraphIcon,
} from "@mui/icons-material";
import EmptyStateCard from "../../../../components/EmptyStateCard";
import useEvents from "../../../../hooks/useEvents";

type EmptyDailyQuestionsProps = {
  onAddQuestion: () => void;
};

const EmptyDailyQuestions = ({ onAddQuestion }: EmptyDailyQuestionsProps) => {
  const events = useEvents();

  return (
    <Box data-testid="EmptyDailyQuestionsView" style={{ margin: 24 }}>
      <EmptyStateCard
        text="Create daily check-in questions to keep clients engaged and track their day-to-day progress and habits."
      />
      <Box
        style={{
          marginTop: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Stack style={{ maxWidth: 500, alignItems: "center" }} spacing={2}>
          <Typography style={{ textAlign: "center", marginBottom: 16 }}>
            Set up custom daily questions that appear in your clients' daily
            reports in the Mighty45 app. Ask about water intake, sleep quality,
            workout completion, mood, or any daily habits you want to monitor.
          </Typography>

          {/* Data Types Information Table */}
          <Box style={{ marginBottom: 24, maxWidth: 600 }}>
            <Typography
              variant="subtitle2"
              style={{ marginBottom: 12, fontWeight: "600" }}
            >
              Supported Question Types:
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Description</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Best For</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Multiline</TableCell>
                    <TableCell>Free-form text input</TableCell>
                    <TableCell>Daily reflections, notes</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Number</TableCell>
                    <TableCell>Numeric input</TableCell>
                    <TableCell>Water intake, sleep hours, steps</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Rating</TableCell>
                    <TableCell>Star rating (1-5)</TableCell>
                    <TableCell>Energy, mood, workout quality</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Scale</TableCell>
                    <TableCell>Slider scale (custom range)</TableCell>
                    <TableCell>Stress levels, motivation</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Yes/No</TableCell>
                    <TableCell>Boolean true/false</TableCell>
                    <TableCell>Workout completed, goals met</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 16,
            }}
          >
            <GraphIcon sx={{ fontSize: 14, mr: 1.0 }} />
            <Typography variant="caption" color="text.secondary">
              Pro tip: Number, Rating, Scale, and Yes/No questions create
              graphable data for tracking trends over time
            </Typography>
          </Box>

          <StandardButton
            data-testid="EmptyDailyQuestionsView-Button"
            variant="primary"
            startIcon={<DailyIcon />}
            onClick={() => {
              onAddQuestion();
              events.logButtonClick({
                buttonName: "Add Your First Daily Question",
                pageName: "Admin",
                tabName: "Daily Questions",
              });
            }}
            style={{ fontSize: 16 }}
          >
            Add Your First Daily Question
          </StandardButton>
        </Stack>
      </Box>
    </Box>
  );
};

export default EmptyDailyQuestions;
